import React from 'react'
import { motion, useMotionValue, useSpring, useTransform, type MotionValue } from 'framer-motion'
import { INTERAX_COLORS } from '../../../constants/colors'
import { emitGlowPointerPosition } from '../hooks/useGlowIntersection'

const LIGHT_SIZE = 520
const SPRING_CONFIG = {
  stiffness: 160,
  damping: 24,
  mass: 0.6
}

interface GlowCursorContextValue {
  isActive: boolean
  cursorMotion: {
    x: MotionValue<number>
    y: MotionValue<number>
  }
}

const GlowCursorContext = React.createContext<GlowCursorContextValue | null>(null)

export const GlowCursorProvider: React.FC<{
  active: boolean
  showGlobalGlow?: boolean
  children: React.ReactNode
}> = ({ active, showGlobalGlow = true, children }) => {
  const rawX = useMotionValue(0)
  const rawY = useMotionValue(0)

  const smoothX = useSpring(rawX, SPRING_CONFIG)
  const smoothY = useSpring(rawY, SPRING_CONFIG)

  const offsetX = useTransform(smoothX, (value) => value - LIGHT_SIZE / 2)
  const offsetY = useTransform(smoothY, (value) => value - LIGHT_SIZE / 2)

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const initializePosition = () => {
      rawX.set(window.innerWidth / 2)
      rawY.set(window.innerHeight / 2)
    }

    initializePosition()

    window.addEventListener('resize', initializePosition)

    return () => {
      window.removeEventListener('resize', initializePosition)
    }
  }, [rawX, rawY])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (!active) return

    const handlePointerMove = (event: MouseEvent | TouchEvent) => {
      if ('touches' in event) {
        const touch = event.touches[0]
        rawX.set(touch.clientX)
        rawY.set(touch.clientY)
        return
      }

      rawX.set(event.clientX)
      rawY.set(event.clientY)
    }

    window.addEventListener('mousemove', handlePointerMove)
    window.addEventListener('touchmove', handlePointerMove, { passive: true })

    return () => {
      window.removeEventListener('mousemove', handlePointerMove)
      window.removeEventListener('touchmove', handlePointerMove)
    }
  }, [active, rawX, rawY])

  React.useEffect(() => {
    if (!active) return

    const unsubscribeX = smoothX.on('change', (value) => {
      emitGlowPointerPosition({ x: value, y: smoothY.get(), timestamp: performance.now() })
    })

    const unsubscribeY = smoothY.on('change', (value) => {
      emitGlowPointerPosition({ x: smoothX.get(), y: value, timestamp: performance.now() })
    })

    return () => {
      unsubscribeX?.()
      unsubscribeY?.()
    }
  }, [active, smoothX, smoothY])

  const contextValue = React.useMemo<GlowCursorContextValue>(() => {
    return {
      isActive: active,
      cursorMotion: {
        x: smoothX,
        y: smoothY
      }
    }
  }, [active, smoothX, smoothY])

  return (
    <GlowCursorContext.Provider value={contextValue}>
      {children}
      {active && showGlobalGlow && (
        <motion.div
          style={{
            x: offsetX,
            y: offsetY,
            width: LIGHT_SIZE,
            height: LIGHT_SIZE,
            background: `radial-gradient(circle at center, ${INTERAX_COLORS.sun} 18%, rgba(255, 171, 64, 0.5) 32%, rgba(3, 105, 161, 0.3) 54%, transparent 80%)`,
            filter: 'blur(48px)',
            opacity: 0.6
          }}
          className="pointer-events-none fixed inset-0 z-0 mix-blend-screen"
        />
      )}
    </GlowCursorContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useGlowCursor = () => {
  const context = React.useContext(GlowCursorContext)

  if (!context) {
    throw new Error('useGlowCursor must be used within a GlowCursorProvider')
  }

  return context
}
